import type { ModuleManifest } from '../types.js'

/**
 * The platform itself: identity, people, permissions, the audit trail and the
 * screens that install every other module. It is the one module that owns the
 * unprefixed resource namespace, and the one that cannot be turned off — the
 * switch that would turn it back on lives inside it.
 */
export const coreModule = {
  key: 'core',
  name: 'Core Platform',
  description: 'Хэрэглэгч, дүр, эрх, үйлдлийн түүх, модулийн удирдлага.',
  version: '2.1.0',
  icon: 'LayersIcon',
  isCore: true,

  permissions: [
    { resource: 'dashboard', action: 'show', description: 'Хяналтын самбар харах' },

    { resource: 'users', action: 'list', description: 'Хэрэглэгчид жагсаах' },
    { resource: 'users', action: 'show', description: 'Хэрэглэгч харах' },
    { resource: 'users', action: 'create', description: 'Хэрэглэгч үүсгэх' },
    { resource: 'users', action: 'edit', description: 'Хэрэглэгч засах' },
    { resource: 'users', action: 'delete', description: 'Хэрэглэгч устгах' },

    { resource: 'roles', action: 'list', description: 'Дүрүүд жагсаах' },
    { resource: 'roles', action: 'show', description: 'Дүр харах' },
    { resource: 'roles', action: 'create', description: 'Дүр үүсгэх' },
    { resource: 'roles', action: 'edit', description: 'Дүр засах, эрх олгох' },
    { resource: 'roles', action: 'delete', description: 'Дүр устгах' },

    { resource: 'permissions', action: 'list', description: 'Эрхийн жагсаалт харах' },

    { resource: 'audit-logs', action: 'list', description: 'Үйлдлийн түүх харах' },
    { resource: 'audit-logs', action: 'show', description: 'Үйлдлийн дэлгэрэнгүй харах' },

    { resource: 'modules', action: 'list', description: 'Модулиудыг харах' },
    { resource: 'modules', action: 'manage', description: 'Модуль идэвхжүүлэх, унтраах' },

    /*
     * `settings:show` нь цэсний «Тохиргоо» бүлгийг хамгаална; `settings:manage`
     * нь дотор нь засварын хэсгүүдийг нээнэ.
     *
     * ! `settings:edit` УСТГАГДСАН: зарлагдсан ч API-д ч, цэсэнд ч, панелд ч
     * ! хэрэглэгдэхгүй байв. Эрхийн матрицад ямар ч үр дагваргүй сэлгүүр
     * ! харагдаж, админ дарсан ч юу ч өөрчлөгддөггүй байлаа.
     *
     * ! `members:manage` МӨН УСТГАГДСАН: гишүүдийн дэлгэц хэрэглэгчийн API-г
     * ! дууддаг ба тэр нь `users:*`-аар хамгаалагддаг. Хоёр эрх нэг зүйлийг
     * ! хариуцвал UI нь нээлттэй, API нь хаалттай (эсвэл эсрэгээр) байх
     * ! боломжтой болно — дэлгэц одоо `users:edit`-ийг шалгана.
     */
    { resource: 'settings', action: 'show', description: 'Тохиргоо харах' },
    { resource: 'settings', action: 'manage', description: 'Тохиргоо бүрэн удирдах' },
  ],

  navigation: [
    {
      label: 'Платформ',
      items: [
        {
          label: 'Хяналтын самбар',
          href: '/',
          icon: 'LayoutDashboardIcon',
          iconClassName: 'text-indigo-600 dark:text-indigo-400',
          permission: 'dashboard:show',
        },
      ],
    },
    {
      label: 'Удирдлага',
      items: [
        {
          label: 'Хэрэглэгчид',
          href: '/settings/members',
          icon: 'UsersIcon',
          iconClassName: 'text-sky-600 dark:text-sky-400',
          permission: 'users:list',
        },
        {
          label: 'Дүр, эрх',
          href: '/settings/roles',
          icon: 'ShieldIcon',
          iconClassName: 'text-violet-600 dark:text-violet-400',
          permission: 'roles:list',
        },
        {
          label: 'Модулиуд',
          href: '/settings/modules',
          icon: 'BlocksIcon',
          iconClassName: 'text-emerald-600 dark:text-emerald-400',
          permission: 'modules:list',
        },
        {
          label: 'Үйлдлийн түүх',
          href: '/settings/activity',
          icon: 'ScrollTextIcon',
          iconClassName: 'text-amber-600 dark:text-amber-400',
          permission: 'audit-logs:list',
        },
      ],
    },
    {
      label: 'Систем',
      items: [
        {
          label: 'Тохиргоо',
          href: '/settings',
          icon: 'SettingsIcon',
          iconClassName: 'text-muted-foreground',
          permission: 'settings:show',
        },
      ],
    },
  ],

  resources: [
    { name: 'dashboard', label: 'Хяналтын самбар', list: '/' },
    {
      name: 'users',
      label: 'Хэрэглэгчид',
      list: '/settings/members',
      create: '/settings/members/create',
      edit: '/settings/members/:id/edit',
      show: '/settings/members/:id',
      canDelete: true,
    },
    {
      name: 'roles',
      label: 'Дүр, эрх',
      list: '/settings/roles',
      create: '/settings/roles/create',
      edit: '/settings/roles/:id/edit',
      show: '/settings/roles/:id',
      canDelete: true,
    },
    { name: 'audit-logs', label: 'Үйлдлийн түүх', list: '/settings/activity', show: '/settings/activity/:id' },
    { name: 'modules', label: 'Модулиуд', list: '/settings/modules' },
  ],
} as const satisfies ModuleManifest
